import React from 'react';
import Card from './Card';
import { Database, MessageSquare, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  formatStorageDisplayString,
} from "../shared-utils";

interface QuotaStatus {
  used: number;
  limit: number;
  withinLimit: boolean;
  percentage: number;
  severity: 'ok' | 'warning' | 'critical';
}

interface QuotaData {
  storage: QuotaStatus;
  queries: QuotaStatus;
  users: QuotaStatus;
  overallStatus: 'ok' | 'warning' | 'critical' | 'exceeded';
}

interface QuotaCardProps {
  quotaData: QuotaData | null;
  loading?: boolean;
}

interface QuotaItem {
  name: string;
  icon: LucideIcon;
  data: QuotaStatus;
  unit: string;
}

const QuotaCard: React.FC<QuotaCardProps> = ({ quotaData, loading = false }) => {
  if (loading) {
    return (
      <>
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-5">
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="h-8 bg-gray-200 rounded w-2/3"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
            </div>
          </Card>
        ))}
      </>
    );
  }

  if (!quotaData) {
    return (
      <>
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-5">
            <p className="text-gray-500 text-sm text-center">Unable to load</p>
          </Card>
        ))}
      </>
    );
  }

  const quotas: QuotaItem[] = [
    { name: 'Storage', icon: Database, data: quotaData.storage, unit: 'GB' },
    { name: 'Queries', icon: MessageSquare, data: quotaData.queries, unit: '' },
    { name: 'Users', icon: Users, data: quotaData.users, unit: '' },
  ];

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'bg-error';
      case 'warning':
        return 'bg-warning';
      case 'ok':
      default:
        return 'bg-success';
    }
  };

  const getSeverityIconColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'text-error';
      case 'warning':
        return 'text-warning';
      case 'ok':
      default:
        return 'text-success';
    }
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  /**
   * Format quota display text with smart unit handling for storage
   */
  const formatQuotaDisplay = (quota: QuotaItem): string => {
    if (quota.name === 'Storage') {
      // Storage values are in bytes, use smart formatting
      return formatStorageDisplayString(quota.data.used, quota.data.limit);
    } else {
      // Queries and Users - use regular number formatting
      return `${formatNumber(quota.data.used)} / ${formatNumber(quota.data.limit)} ${quota.unit}`;
    }
  };

  return (
    <>
      {quotas.map((quota) => (
        <Card key={quota.name} className="p-5">
          <div className="flex items-center space-x-4 mb-3">
            <div className={`p-3 rounded-full ${getSeverityIconColor(quota.data.severity).replace('text-', 'bg-')}/10`}>
              <quota.icon className={`w-6 h-6 ${getSeverityIconColor(quota.data.severity)}`} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">{quota.name}</p>
              <p className={`text-xl font-semibold ${getSeverityIconColor(quota.data.severity)}`}>
                {formatQuotaDisplay(quota)}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getSeverityColor(
                quota.data.severity
              )}`}
              style={{ width: `${Math.min(quota.data.percentage, 100)}%` }}
            ></div>
          </div>

          {/* Percentage and Status */}
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium ${getSeverityIconColor(quota.data.severity)}`}>
              {quota.data.percentage.toFixed(1)}% used
            </span>
            {quota.data.severity !== 'ok' && (
              <span className="text-xs text-gray-500">
                {quota.data.severity === 'critical' ? '⚠️ Critical' : '⚠️ Warning'}
              </span>
            )}
          </div>
        </Card>
      ))}
    </>
  );
};

export default QuotaCard;
